import React, {useEffect, useRef, useState} from 'react';
import {Animated, Easing, StyleSheet, View} from 'react-native';
import Svg, {Circle, Polygon, Polyline} from 'react-native-svg';
import CCText from '@/Atoms/CCText';
import ThunderText from './ThunderText';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

// Gold/amber, matching the rating card's warm theme. Exact values from the
// design handoff's sparkline: stroke #D1AB41, fill rgba(250,207,71,.20).
const SPARKLINE_STROKE = '#D1AB41';
const SPARKLINE_FILL = 'rgba(250,207,71,0.32)';

// For the sparkline draw-in (spec §5: stroke-dashoffset 1400->0, 1.5s
// ease-out, once; fill opacity 0->1, 1.4s).
const AnimatedPolyline = Animated.createAnimatedComponent(Polyline);
const AnimatedPolygon = Animated.createAnimatedComponent(Polygon);
const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const styles = StyleSheet.create({
  // The screen is edge-to-edge (no centered cap), so a fixed 376px rating card
  // looked far narrower than the design once the identity column absorbed the
  // extra width on wide monitors. Give the card a proportional width (~a third
  // of the hero, matching the design's rating/identity split) with a generous
  // 440px floor so it stays wide at every viewport, and a ceiling so the
  // sparkline doesn't sprawl on ultra-wide displays. It wraps full-width below
  // the identity block only on a genuinely narrow viewport.
  ratingCard: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '33%',
    minWidth: 440,
    maxWidth: 760,
    backgroundColor: '#FAF6EB',
    borderRadius: 16,
    padding: 20,
  },
  // Mobile/tablet override: the 440px floor above is wider than the whole
  // viewport, forcing overflow/clipping -- take the full row width instead.
  ratingCardMobile: {
    flexBasis: 'auto',
    minWidth: 0,
    maxWidth: '100%',
    width: '100%',
  },
  ratingCardLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#888280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  ratingCardTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  // Font size/line-height come from ThunderText (76px / 0.86 line-height per
  // spec §1.2). This only carries the color + spacing.
  ratingCardValue: {
    color: '#262322',
    marginTop: 6,
  },
  ratingMetaCol: {
    alignItems: 'flex-end',
  },
  ratingPeak: {
    fontSize: 11,
    color: '#888280',
    marginTop: 7,
  },
  ratingDeltaBadge: {
    borderRadius: 999,
    paddingVertical: 3,
    paddingHorizontal: 8,
  },
  ratingDeltaBadgeUp: {
    backgroundColor: '#E4F6EB',
  },
  ratingDeltaBadgeDown: {
    backgroundColor: '#FFEBEE',
  },
  ratingDeltaBadgeFlat: {
    backgroundColor: '#F0EDE7',
  },
  ratingDeltaTextUp: {
    fontSize: 11,
    fontWeight: '700',
    color: '#2F7F4C',
  },
  ratingDeltaTextDown: {
    fontSize: 11,
    fontWeight: '700',
    color: '#CD2C39',
  },
  ratingDeltaTextFlat: {
    fontSize: 11,
    fontWeight: '700',
    color: '#888280',
  },
  sparklineWrap: {
    width: '100%',
    marginTop: 18,
  },
  sparklineLabel: {
    fontSize: 11,
    color: '#A8A7A6',
    marginTop: 6,
    alignSelf: 'flex-start',
  },
});

// DEVIATION from the plan's "reuse `src/Components/Graph/index.tsx`" note:
// Graph hardcodes a 0-100 y-axis domain and a "%"-suffixed tooltip (it's
// built for accuracy-percentage series in the Assessments screens) and reads
// its container size from a *global* zustand store
// (`useGraphDimensionsStore`) that a different screen (AssessmentDetails)
// also writes to. Rating values (e.g. 800-2400) don't fit a 0-100 domain,
// and there's no way to override it without editing the shared component.
// A tiny purpose-built SVG polyline sparkline is used instead — flagged here
// as a deliberate deviation, not a silent workaround.
function RatingSparkline({
  points,
}: {
  points: {date: string | null; rating: number | null}[];
}) {
  // Fill the rating card's full width (design's sparkline is width:100% of a
  // 376px card, not a fixed narrow SVG). Measure the container via onLayout
  // and draw in real pixel coords so the end dot stays a circle (a viewBox +
  // preserveAspectRatio:none stretch would distort it into an ellipse).
  const [width, setWidth] = useState(0);
  const draw = useRef(new Animated.Value(0)).current;
  const height = 108;
  const values = points
    .map(p => p.rating)
    .filter((v): v is number => v != null);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  const flat = max === min;
  const yFor = (v: number) =>
    flat ? height / 2 : height - ((v - min) / (max - min)) * height;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const coords = values.map((v, i) => `${i * step},${yFor(v)}`).join(' ');
  const areaPoints = `0,${height} ${coords} ${width},${height}`;
  // Approximate polyline length, for the dashoffset draw-in.
  let pathLen = 0;
  for (let i = 1; i < values.length; i++) {
    pathLen += Math.hypot(step, yFor(values[i]) - yFor(values[i - 1]));
  }
  pathLen = Math.max(pathLen, 1);

  useEffect(() => {
    if (width <= 0) {
      return;
    }
    draw.setValue(0);
    const anim = Animated.timing(draw, {
      toValue: 1,
      duration: 1500,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    });
    anim.start();
    return () => anim.stop();
  }, [draw, width, coords]);

  if (values.length < 2) {
    return null;
  }
  const dashOffset = draw.interpolate({
    inputRange: [0, 1],
    outputRange: [pathLen, 0],
  });
  const fillOpacity = draw.interpolate({
    inputRange: [0, 0.45, 1],
    outputRange: [0, 0.2, 1],
  });
  const dotOpacity = draw.interpolate({
    inputRange: [0, 0.85, 1],
    outputRange: [0, 0, 1],
  });
  return (
    <View
      style={styles.sparklineWrap}
      onLayout={e => setWidth(e.nativeEvent.layout.width)}>
      {width > 0 ? (
        // Always render the filled area + line, including the flat case
        // (yFor pins a flat window to mid-height): the design shows the
        // colored area under the curve even when the rating hasn't moved.
        <Svg width={width} height={height}>
          <AnimatedPolygon
            points={areaPoints}
            fill={SPARKLINE_FILL}
            opacity={fillOpacity}
            stroke="none"
          />
          <AnimatedPolyline
            points={coords}
            fill="none"
            stroke={SPARKLINE_STROKE}
            strokeWidth={2.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={pathLen}
            strokeDashoffset={dashOffset}
          />
          <AnimatedCircle
            cx={Math.max(0, (values.length - 1) * step - 2)}
            cy={yFor(values[values.length - 1])}
            r={3}
            fill={SPARKLINE_STROKE}
            opacity={dotOpacity}
          />
        </Svg>
      ) : null}
      <CCText style={styles.sparklineLabel}>
        Last {values.length} rated games{flat ? ' · no change' : ''}
      </CCText>
    </View>
  );
}

// Design's rating card shows a "+24 · 30d" delta badge -- the backend only
// gives us the last N rated games, not a calendar-day window, so the delta
// is computed over whatever history is available and labelled honestly
// (see RatingSparkline's "Last N rated games" caption) rather than claiming
// a "30d" figure the data doesn't back.
//
// Shared between ProfileHeader (logged-in/owner view) and AnonymousHeader
// (logged-out visitor view) -- both need the identical full-size card, not
// two divergent implementations.
export default function RatingCard({
  rating,
  history,
}: {
  rating: number | null;
  history: {date: string | null; rating: number | null}[];
}) {
  const values = history
    .map(p => p.rating)
    .filter((v): v is number => v != null);
  const delta =
    values.length >= 2 ? values[values.length - 1] - values[0] : null;
  // Peak over the available window -- the design shows "K 32 · peak 1854";
  // we don't get a K-factor from this payload, so we show peak alone
  // (computed, honest) rather than inventing a K value.
  const peak = values.length ? Math.max(...values) : null;

  // The 440px floor is meant to keep the card wide on desktop/tablet -- on a
  // real mobile viewport it just forces horizontal overflow/clipping (the
  // card demands more width than the screen has). Drop the floor/ceiling and
  // let it size to whatever width it wraps into instead.
  const {isMobileOrTab} = useMediaQuery();

  return (
    <View
      style={[
        styles.ratingCard,
        isMobileOrTab && styles.ratingCardMobile,
      ]}>
      <View style={styles.ratingCardTopRow}>
        <View>
          <CCText style={styles.ratingCardLabel}>CircleChess rating</CCText>
          <ThunderText size={76} lineHeight={66} style={styles.ratingCardValue}>
            {rating ?? '—'}
          </ThunderText>
        </View>
        {delta != null || peak != null ? (
          <View style={styles.ratingMetaCol}>
            {delta != null ? (
              <View
                style={[
                  styles.ratingDeltaBadge,
                  delta === 0
                    ? styles.ratingDeltaBadgeFlat
                    : delta > 0
                    ? styles.ratingDeltaBadgeUp
                    : styles.ratingDeltaBadgeDown,
                ]}>
                <CCText
                  style={
                    delta === 0
                      ? styles.ratingDeltaTextFlat
                      : delta > 0
                      ? styles.ratingDeltaTextUp
                      : styles.ratingDeltaTextDown
                  }>
                  {delta > 0 ? '+' : ''}
                  {delta}
                </CCText>
              </View>
            ) : null}
            {peak != null ? (
              <CCText style={styles.ratingPeak}>peak {peak}</CCText>
            ) : null}
          </View>
        ) : null}
      </View>
      <RatingSparkline points={history} />
    </View>
  );
}
